import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { 
  MessageSquare, Plus, Edit, Trash2, Loader2, Settings,
  AlertCircle, CheckCircle2, Phone, FileText, Copy
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SMSSettingsManager = () => {
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateData, setTemplateData] = useState({
    name: '',
    content: '',
    category: 'general',
    description: ''
  });
  const [saving, setSaving] = useState(false);

  const getToken = () => localStorage.getItem('token');

  const fetchSettings = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/sms/settings`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching SMS settings:', error);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/sms/templates`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchTemplates();
  }, [fetchSettings, fetchTemplates]);

  const handleSaveTemplate = async () => {
    if (!templateData.name || !templateData.content) {
      toast.error('Name and content are required');
      return;
    }

    setSaving(true);
    try {
      const url = editingTemplate
        ? `${API_URL}/api/sms/templates/${editingTemplate.id}`
        : `${API_URL}/api/sms/templates`;

      const response = await fetch(url, {
        method: editingTemplate ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(templateData)
      });

      if (response.ok) {
        toast.success(editingTemplate ? 'Template updated' : 'Template created');
        setShowTemplateDialog(false);
        setEditingTemplate(null);
        setTemplateData({ name: '', content: '', category: 'general', description: '' });
        fetchTemplates();
      } else {
        toast.error('Failed to save template');
      }
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Delete this template?')) return;

    try {
      const response = await fetch(`${API_URL}/api/sms/templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      if (response.ok) {
        toast.success('Template deleted');
        fetchTemplates();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete template');
      }
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateData({
      name: template.name,
      content: template.content,
      category: template.category,
      description: template.description || ''
    });
    setShowTemplateDialog(true);
  };

  const openNewTemplate = () => {
    setEditingTemplate(null);
    setTemplateData({ name: '', content: '', category: 'general', description: '' });
    setShowTemplateDialog(true);
  };

  const categoryColors = {
    general: 'bg-slate-100 text-slate-700',
    appointment: 'bg-blue-100 text-blue-700',
    follow_up: 'bg-green-100 text-green-700',
    reminder: 'bg-amber-100 text-amber-700',
    intake: 'bg-purple-100 text-purple-700'
  };

  const categoryLabels = {
    general: 'General',
    appointment: 'Appointment',
    follow_up: 'Follow-up',
    reminder: 'Reminder',
    intake: 'Intake'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        <span>Loading SMS settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Twilio Configuration Status */}
      <Card className={settings?.twilio_configured ? 'border-green-200 bg-green-50/50' : 'border-amber-200 bg-amber-50/50'}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${settings?.twilio_configured ? 'bg-green-100' : 'bg-amber-100'}`}>
                <Phone className={`w-5 h-5 ${settings?.twilio_configured ? 'text-green-600' : 'text-amber-600'}`} />
              </div>
              <div>
                <CardTitle className="text-base">Twilio Configuration</CardTitle>
                <CardDescription>
                  {settings?.twilio_configured 
                    ? `Connected: ${settings.twilio_phone_number}` 
                    : 'SMS provider not configured'}
                </CardDescription>
              </div>
            </div>
            <Badge className={settings?.twilio_configured ? 'bg-green-500' : 'bg-amber-500'}>
              {settings?.twilio_configured ? (
                <><CheckCircle2 className="w-3 h-3 mr-1" /> Active</>
              ) : (
                <><AlertCircle className="w-3 h-3 mr-1" /> Demo Mode</>
              )}
            </Badge>
          </div>
        </CardHeader>
        {!settings?.twilio_configured && (
          <CardContent className="pt-0">
            <div className="p-3 bg-amber-100 rounded-lg text-sm text-amber-800">
              <p className="font-medium mb-2">To enable SMS messaging, add the following to your environment:</p>
              <ul className="list-disc list-inside space-y-1 text-xs font-mono">
                <li>TWILIO_ACCOUNT_SID={settings?.credentials_needed?.TWILIO_ACCOUNT_SID ? '✓' : '(required)'}</li>
                <li>TWILIO_AUTH_TOKEN={settings?.credentials_needed?.TWILIO_AUTH_TOKEN ? '✓' : '(required)'}</li>
                <li>TWILIO_PHONE_NUMBER={settings?.credentials_needed?.TWILIO_PHONE_NUMBER ? '✓' : '(required)'}</li>
              </ul>
            </div>
          </CardContent>
        )}
      </Card>

      {/* SMS Templates */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                SMS Templates
              </CardTitle>
              <CardDescription>
                Create reusable message templates for common communications
              </CardDescription>
            </div>
            <Button onClick={openNewTemplate} data-testid="add-sms-template-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Template
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {templates.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No SMS templates yet</p>
              <Button variant="outline" className="mt-4" onClick={openNewTemplate}>
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Template
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="flex items-start justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                  data-testid={`sms-template-${template.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{template.name}</span>
                      <Badge className={categoryColors[template.category] || categoryColors.general}>
                        {categoryLabels[template.category] || template.category}
                      </Badge>
                      {template.is_default && (
                        <Badge variant="outline" className="text-xs">Default</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {template.content}
                    </p>
                    {template.variables?.length > 0 && (
                      <div className="flex gap-1 mt-2 flex-wrap">
                        {template.variables.map((v) => (
                          <code key={v} className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">
                            {`{{${v}}}`}
                          </code>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 ml-4">
                    <Button variant="ghost" size="icon" onClick={() => openEditTemplate(template)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    {!template.is_default && (
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteTemplate(template.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Template Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {editingTemplate ? 'Edit Template' : 'New SMS Template'}
            </DialogTitle>
            <DialogDescription>
              Create a reusable message template. Use {'{{variable}}'} for dynamic content.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Template Name *</Label>
              <Input
                value={templateData.name}
                onChange={(e) => setTemplateData({ ...templateData, name: e.target.value })}
                placeholder="e.g., Appointment Reminder"
              />
            </div>

            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={templateData.category}
                onValueChange={(v) => setTemplateData({ ...templateData, category: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="appointment">Appointment</SelectItem>
                  <SelectItem value="follow_up">Follow-up</SelectItem>
                  <SelectItem value="reminder">Reminder</SelectItem>
                  <SelectItem value="intake">Intake</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Message Content *</Label>
              <Textarea
                value={templateData.content}
                onChange={(e) => setTemplateData({ ...templateData, content: e.target.value })}
                placeholder="Hi {{client_name}}, this is a reminder about..."
                rows={4}
                maxLength={1600}
              />
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{templateData.content.length}/1600 characters</span>
                <span>Use {'{{variable}}'} for merge fields</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Input
                value={templateData.description}
                onChange={(e) => setTemplateData({ ...templateData, description: e.target.value })}
                placeholder="When to use this template"
              />
            </div>

            {/* Available Variables */}
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs font-medium text-muted-foreground mb-2">Common Variables:</p>
              <div className="flex flex-wrap gap-2">
                {['client_name', 'case_number', 'appointment_date', 'appointment_time', 'amount', 'due_date'].map((v) => (
                  <Button
                    key={v}
                    variant="outline"
                    size="sm"
                    className="text-xs h-6"
                    onClick={() => setTemplateData({ 
                      ...templateData, 
                      content: templateData.content + `{{${v}}}` 
                    })}
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    {v}
                  </Button>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTemplate} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {editingTemplate ? 'Update' : 'Create'} Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SMSSettingsManager;
